/**
 * Studio Team songs carry a header before the lyrics: [arrangement_cues |...], [Character_Dossier],
 * then the bleed token line (slashes, stars, slashes). Parliament Polish reviews lyrics only, and its physics-layer
 * stripper does not know about that header, so it is split off before polishing and put back after.
 */

const BLEED_LINE = /^\/\/\/\*+\/\/\/[ \t]*$/m;

export interface ScaffoldSplit {
  /** The cue/dossier header including its bleed token, or '' when the song has none. */
  scaffold: string;
  /** What is left to polish: the sung lyrics (with their inline tags). */
  lyrics: string;
}

/** True when `text` begins with a Studio Team header rather than plain lyrics or physics layers. */
function looksLikeStudioTeamHeader(header: string): boolean {
  return /\[arrangement_cues\b/i.test(header) || /\[Character_Dossier\]/i.test(header);
}

/** Splits a Vault song into its Studio Team header and its lyrics. Songs without a header come back whole. */
export function splitStudioTeamScaffold(text: string): ScaffoldSplit {
  const source = text ?? '';
  const m = BLEED_LINE.exec(source);
  if (!m) return { scaffold: '', lyrics: source.trim() };
  const header = source.slice(0, m.index).trim();
  if (!looksLikeStudioTeamHeader(header)) return { scaffold: '', lyrics: source.trim() };
  return {
    scaffold: `${header}\n\n${m[0].trim()}`,
    lyrics: source.slice(m.index + m[0].length).trim(),
  };
}

/** Puts a saved header back in front of freshly polished text. Never doubles a header already there. */
export function attachStudioTeamScaffold(scaffold: string, polished: string): string {
  if (!scaffold) return polished;
  if (/\[arrangement_cues\b/i.test(polished) || /\[Character_Dossier\]/i.test(polished)) return polished;
  return `${scaffold}\n\n${polished}`;
}
